import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { changePassword, deleteAccount } from '../../api';
import { errorMessage } from '../../lib/api';
import { Callout } from '../../components/Callout';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Flash } from '../../components/Flash';
import { Spinner } from '../../components/Spinner';
import { TwoFactorSettings } from '../../components/TwoFactorSettings';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useAuth } from '../../auth/useAuth';
import { SettingsSection } from './common';

function ChangePasswordForm() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mismatch, setMismatch] = useState(false);

  const mutation = useMutation({
    mutationFn: () => changePassword(current, next),
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setConfirm('');
      setMismatch(false);
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (next !== confirm) {
      setMismatch(true);
      return;
    }
    setMismatch(false);
    mutation.mutate();
  };

  return (
    <div className="card p-5">
      <h3 className="mb-3 text-sm font-semibold text-ink">Change password</h3>
      <form onSubmit={onSubmit} className="flex flex-col gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-ink-muted">
            Current password
          </label>
          <input
            type="password"
            className="input"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            required
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-ink-muted">
            New password
          </label>
          <input
            type="password"
            className="input"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-ink-muted">
            Confirm new password
          </label>
          <input
            type="password"
            className="input"
            value={confirm}
            onChange={(e) => {
              setConfirm(e.target.value);
              setMismatch(false);
            }}
            autoComplete="new-password"
            minLength={8}
            required
          />
        </div>
        {mismatch && (
          <Callout tone="danger">Passwords do not match.</Callout>
        )}
        {mutation.isError && (
          <Callout tone="danger">
            {errorMessage(mutation.error, 'Could not change password')}
          </Callout>
        )}
        {mutation.isSuccess && (
          <Flash duration={4000} onDone={() => mutation.reset()}>
            Password updated.
          </Flash>
        )}
        <div className="flex justify-end">
          <button
            type="submit"
            className="btn-primary"
            disabled={mutation.isPending || !current || !next || !confirm}
          >
            {mutation.isPending && <Spinner className="h-3.5 w-3.5" />}
            Update password
          </button>
        </div>
      </form>
    </div>
  );
}

function DeleteAccountSection() {
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');

  const del = useMutation({
    mutationFn: () => deleteAccount(password),
    // logout() navigates away itself (full page load to the chooser).
    onSuccess: () => logout(),
  });

  return (
    <div>
      <p className="text-sm text-ink-muted">
        This can’t be undone. Workspaces where you’re the only member are deleted
        with all their data. If you solely own a workspace others are in, you’ll
        be asked to hand it off or delete it first.
      </p>
      <button
        type="button"
        className="btn-secondary mt-3 text-danger"
        onClick={() => setOpen(true)}
      >
        Delete account
      </button>
      <ConfirmDialog
        open={open}
        title="Delete account?"
        confirmLabel="Delete my account"
        busy={del.isPending}
        error={del.isError ? errorMessage(del.error, 'Could not delete your account') : null}
        onConfirm={() => {
          if (password) del.mutate();
        }}
        onClose={() => {
          setOpen(false);
          setPassword('');
          del.reset();
        }}
        message={
          <div className="space-y-3 text-left">
            <p>
              Enter your password to permanently delete your account. This can’t
              be undone.
            </p>
            <input
              type="password"
              autoComplete="current-password"
              className="input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
              aria-label="Password"
            />
          </div>
        }
      />
    </div>
  );
}

export function SecuritySettingsSection() {
  useDocumentTitle('Settings · Security');

  return (
    <div className="space-y-6">
      <SettingsSection
        title="Password"
        description="Change the password you use to sign in."
      >
        <ChangePasswordForm />
      </SettingsSection>
      <SettingsSection
        title="Two-factor authentication"
        description="Two-factor authentication (TOTP) adds a second step at sign-in using an authenticator app."
      >
        <TwoFactorSettings />
      </SettingsSection>
      <SettingsSection
        title="Delete account"
        description="Permanently delete your account and any workspaces that are only yours."
      >
        <DeleteAccountSection />
      </SettingsSection>
    </div>
  );
}
